import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface Activity {
  id: string;
  name: string;
  description: string;
  category: string;
  duration: string;
  price: number;
  currency: string;
  rating: number;
  reviewCount: number;
  location: {
    name: string;
    address: string;
    lat: number;
    lng: number;
  };
  images: string[];
  availability: string[];
  highlights: string[];
  included: string[];
  notIncluded: string[];
  cancellationPolicy: string;
  bookingUrl: string;
}

interface UseActivitiesSearchReturn {
  results: Activity[];
  search: (params: {
    latitude: number;
    longitude: number;
    radius?: number;
    keyword?: string;
    categories?: string[];
    priceRange?: string;
  }) => Promise<void>;
  loading: boolean;
  error: string | null;
  demoMode: boolean;
  clearResults: () => void;
  clearError: () => void;
}

export function useActivitiesSearch(): UseActivitiesSearchReturn {
  const [results, setResults] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [demoMode, setDemoMode] = useState(false);

  const search = useCallback(async (params: {
    latitude: number;
    longitude: number;
    radius?: number;
    keyword?: string;
    categories?: string[];
    priceRange?: string;
  }): Promise<void> => {
    setLoading(true);
    setError(null);
    setDemoMode(false);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/search-activities`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${supabaseAnonKey}`,
            'Content-Type': 'application/json',
            'apikey': supabaseAnonKey,
          },
          body: JSON.stringify(params),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Activities API error:', response.status, errorText);
        throw new Error(errorText || `Failed to search activities (HTTP ${response.status})`);
      }

      const data = await response.json();
      console.log('Activities API response:', data);

      if (data.error) {
        console.error('Activities API error message:', data.error);
        setError(data.message || data.error);
        setResults([]);
        return;
      }

      const activities = data.activities || [];
      setResults(activities);

      if (activities.length === 0) {
        // If no activities found and it's not a success, show info message
        if (!data.success) {
          setError(data.message || 'No activities found in this area. Try a different location.');
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to search activities';
      console.error('Activities search error:', err);
      setError(message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearResults = useCallback(() => {
    setResults([]);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    results,
    search,
    loading,
    error,
    demoMode,
    clearResults,
    clearError,
  };
}
